/**
 * FocusChipRow — composer chip row surfacing the user's focused sections.
 *
 * Fully controlled: the caller owns the focus list (the same state that feeds
 * the agent's focusedSections), so on remove/clear we:
 *   1) call `onChange` so the composer's local state updates,
 *   2) dispatch `xavior:edit-sections-changed` so useChat sees the update,
 *   3) dispatch `xavior:deselect-section` so PageRenderer forwards the
 *      deselect to the in-iframe inspector and the highlight clears too.
 *
 * Renders nothing when `sections` is empty.
 */
import React from 'react';

export interface FocusedSection {
  sectionId: string;
  label: string;
}

export interface FocusChipRowProps {
  sections: FocusedSection[];
  pageTitle?: string | null;
  onChange: (next: FocusedSection[]) => void;
  /** Test override for window.dispatchEvent — lets unit tests assert on
   *  which events fired without touching the real event bus. */
  dispatchEvent?: (event: Event) => boolean;
}

export default function FocusChipRow({
  sections,
  pageTitle,
  onChange,
  dispatchEvent,
}: FocusChipRowProps) {
  if (!sections.length) return null;

  const dispatch =
    dispatchEvent ?? ((event: Event) => window.dispatchEvent(event));

  const emitChanged = (next: FocusedSection[]) => {
    dispatch(
      new CustomEvent('xavior:edit-sections-changed', {
        detail: { sections: next },
      }),
    );
  };

  const handleRemove = (sectionId: string) => {
    const next = sections.filter((s) => s.sectionId !== sectionId);
    onChange(next);
    emitChanged(next);
    // Tell PageRenderer to clear the iframe highlight for this section.
    // Inspector stays active — the user can still click the page to add
    // new sections to focus.
    dispatch(
      new CustomEvent('xavior:deselect-section', {
        detail: { sectionId },
      }),
    );
  };

  const handleClearAll = () => {
    onChange([]);
    emitChanged([]);
    // Undirected deselect = clear all highlights in the iframe.
    dispatch(new CustomEvent('xavior:deselect-section', { detail: {} }));
  };

  return (
    <div
      className="chat-edit-sections-tray"
      data-testid="chat-focus-chip-row"
    >
      <span className="chat-edit-sections-label">
        Editing section(s):
      </span>
      {sections.map((sec) => (
        <span
          key={sec.sectionId}
          className="chat-edit-section-pill"
          data-testid={`chat-focus-chip-${sec.sectionId}`}
        >
          <span className="chat-edit-section-pill-label">{sec.label}</span>
          <button
            type="button"
            className="chat-edit-section-pill-remove"
            onClick={() => handleRemove(sec.sectionId)}
            aria-label={`Remove ${sec.label}`}
            data-testid={`chat-focus-chip-remove-${sec.sectionId}`}
          >
            <svg
              viewBox="0 0 24 24"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </span>
      ))}
      <button
        type="button"
        className="chat-edit-sections-clear"
        onClick={handleClearAll}
        aria-label="Clear all focused sections"
        data-testid="chat-focus-clear-all"
      >
        Clear
      </button>
    </div>
  );
}
